import React from 'react'
import {Box, Typography} from '@mui/material'
import moment from 'moment'
import {ColumnDef} from 'src/components-new/data/DataTable'
import ActionIconButton from 'src/components-new/primitives/ActionIconButton'

export type DocRecord = {
  _id: string
  name?: string
  fileName?: string
  fileId?: string
  category?: string[]
  uploadDate?: string
  type?: string
  size?: number
  description?: string
  creatorId?: string
  user?: {firstName?: string; lastName?: string}
  categoryReadable?: string
  [key: string]: any
}

interface RowActionsMenuProps {
  row: DocRecord
  onInfo: (row: DocRecord) => void
  onView: (row: DocRecord) => void
  onDelete: (row: DocRecord) => void
  authUserId?: string
}

function RowActionsMenu({row, onInfo, onView, onDelete, authUserId}: RowActionsMenuProps) {
  return (
    <Box onClick={(e) => e.stopPropagation()} sx={{display: 'flex', gap: 0.5}}>
      <ActionIconButton variant="info" tooltip="Info" onClick={() => onInfo(row)} />
      <ActionIconButton variant="download" tooltip="Scarica" onClick={() => onView(row)} />
      {row.creatorId === authUserId && (
        <ActionIconButton variant="delete" tooltip="Elimina" onClick={() => onDelete(row)} />
      )}
    </Box>
  )
}

interface BuildColumnsOptions {
  onInfo: (row: DocRecord) => void
  onView: (row: DocRecord) => void
  onDelete: (row: DocRecord) => void
  authUserId?: string
  columnOptions?: {id: string; label: string; type?: string; format?: string}[]
}

export function buildColumns({
  onInfo,
  onView,
  onDelete,
  authUserId,
  columnOptions,
}: BuildColumnsOptions): ColumnDef<DocRecord>[] {
  const actionsCol: ColumnDef<DocRecord> = {
    key: 'actions',
    header: '',
    width: 112,
    isActionColumn: true,
    render: (row) => (
      <RowActionsMenu
        row={row}
        onInfo={onInfo}
        onView={onView}
        onDelete={onDelete}
        authUserId={authUserId}
      />
    ),
  }

  if (columnOptions && columnOptions.length > 0) {
    const extraCols: ColumnDef<DocRecord>[] = columnOptions.map((col) => {
      // Alcune configurazioni PSP indicano la colonna data con id diversi da 'uploadDate'
      // (es. 'createdAt', legacy dai config Table del vecchio stack): ci si basa su
      // `type: 'date'` per non lasciarla in fallback come stringa ISO grezza.
      if (col.id === 'uploadDate' || col.type === 'date') {
        return {
          key: col.id,
          header: col.label,
          render: (row: DocRecord) => {
            const value = row.uploadDate ?? (row as any)[col.id]
            return (
              <Typography variant="body2">
                {value ? moment(value).format(col.format ?? 'DD MMMM YYYY, HH:mm') : '—'}
              </Typography>
            )
          },
        }
      }
      if (col.id === 'name') {
        return {
          key: col.id,
          header: col.label,
          hideInMobileCard: true,
          render: (row: DocRecord) => (
            <Typography variant="body2">{row.name ?? row.fileName ?? '—'}</Typography>
          ),
        }
      }
      return {
        key: col.id,
        header: col.label,
        render: (row: DocRecord) => (
          <Typography variant="body2">{(row as any)[col.id] ?? '—'}</Typography>
        ),
      }
    })
    return [...extraCols, actionsCol]
  }

  const defaultCols: ColumnDef<DocRecord>[] = [
    {
      key: 'categoryReadable',
      header: 'CATEGORIA',
      render: (row) => {
        const cats = row.categoryReadable ? row.categoryReadable.split(', ') : ['—']
        return (
          <Box>
            {cats.map((cat: string, i: number) => (
              <Typography key={i} variant="body2">{cat}</Typography>
            ))}
          </Box>
        )
      },
    },
    {
      key: 'uploadDate',
      header: 'CARICATO IL',
      render: (row) => (
        <Typography variant="body2">
          {row.uploadDate ? moment(row.uploadDate).format('DD MMMM YYYY, HH:mm') : '—'}
        </Typography>
      ),
    },
    {
      key: 'name',
      header: 'NOME',
      hideInMobileCard: true,
      render: (row) => (
        <Typography variant="body2">{row.name ?? row.fileName ?? '—'}</Typography>
      ),
    },
    {
      key: 'type',
      header: 'TIPO',
      render: (row) => (
        <Typography variant="body2">{row.type ?? '—'}</Typography>
      ),
    },
    actionsCol,
  ]

  return defaultCols
}
